/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: caoran
 * @LastEditTime: 2024-12-22 11:33:28
 */
import './index.scss'
import React, { forwardRef, useState, useEffect, useMemo, useRef } from 'react'
import logoImg from '../../../images/todaycam_logo.png'
import footerImg from '../../../images/jigsaw-examples/jigsaw_footer.png'
import copilotIcon from '../../../images/jigsaw-examples/copilot_icon.png'
import CONFIG from '../../../constants/config'
import { useAppSelector } from '../../../app/hooks'
import AddPhotos from '../AddPhotos'
import { workProgress } from '../lib'
import AddPhotoTour from '../AddPhotoTour'
import Table from './Table'
import type { JigsawPhoto, JigsawData, TableConfig } from '../types'

interface PhotoProps {
  photo: JigsawPhoto
  layout?: number
}

const Photo = ({ photo, layout = 1 }: PhotoProps) => {
  const url = useMemo(() => {
    if (photo.url) {
      return photo.url + CONFIG.ossImgProcess900
    }
    if (photo.file) {
      const host = photo.file.includes('tencent-')
        ? CONFIG.tencentPhotoPrefix
        : CONFIG.photoPrefix
      return host + photo.file + CONFIG.ossImgProcess900
    }
  }, [photo])
  return (
    <div className={`photo layout-${layout}-photo`}>
      <img src={url} alt="" />
    </div>
  )
}

interface AIContentProps {
  photos: JigsawPhoto[]
  copilotSwitch: boolean
}

const AIContent = ({ photos, copilotSwitch }: AIContentProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const num = photos.length
  const realNum = photos.filter(
    (item) => item.sourceType === 0
  ).length
  const percent = useMemo(() => {
    if ((realNum / num) % 1 === 0) {
      return (realNum / num) * 100
    } else {
      return ((realNum / num) * 100).toFixed(2)
    }
  }, [num, realNum])

  const text = useMemo(() => {
    if (num) {
      return `本次汇报共${num}张照片，${realNum}张由今日水印相机拍摄，真实时间照片占比${percent}%`
    } else {
      return '请点击下方「添加汇报照片」按钮选择照片，将帮您生成照片统计信息'
    }
  }, [num, realNum, percent])

  return (
    <div
      ref={ref}
      className="ai-content between-lines-item"
      style={{
        height: copilotSwitch ? (ref.current?.scrollHeight ?? 0) : 0,
        margin: copilotSwitch ? '0 auto 20px' : '0 auto'
      }}
    >
      <img className="copilot-icon" src={copilotIcon} />
      <div className="high-light">{text}</div>
    </div>
  )
}

interface ReportInfoProps {
  jigsawData: JigsawData
}

const ReportInfo = ({ jigsawData }: ReportInfoProps) => {
  const { date, reportor, mobile, company, avatarSwitch } = jigsawData
  const headImageURL = useAppSelector((state) =>
    state.main.user?.headimgurl
  )
  if (date && reportor && mobile && company && avatarSwitch) {
    return (
      <div className="report-info-wrapper report-info-all-wrapper">
        <div className="left">
          <img className="avatar" src={headImageURL} alt="" />
          <div>
            <div className="report-info-item">{reportor}</div>
            <div className="report-info-item">{mobile}</div>
          </div>
        </div>
        <div className="right">
          <div className="report-info-item">{company}</div>
          <div className="report-info-item">{date}</div>
        </div>
      </div>
    )
  }
  if (date || reportor || mobile || company) {
    return (
      <div className="report-info-wrapper report-info-no-avatar">
        {reportor && mobile && (
          <div className="report-info-item">
            {avatarSwitch && (
              <img className="avatar" src={headImageURL} alt="" />
            )}
            {reportor}（{mobile}）
          </div>
        )}
        {reportor && !mobile && (
          <div className="report-info-item">
            {avatarSwitch && (
              <img className="avatar" src={headImageURL} alt="" />
            )}
            {reportor}
          </div>
        )}
        {!reportor && mobile && (
          <div className="report-info-item">{mobile}</div>
        )}
        {company && <div className="report-info-item">{company}</div>}

        {date && <div className="report-info-item">{date}</div>}
      </div>
    )
  }
  return null
}

interface WorkProgressProps {
  progressSwitch: boolean
  progress: number
}

const WorkProgress = ({ progressSwitch, progress }: WorkProgressProps) => {
  const progressText = useMemo(() => {
    return workProgress[progress].text
  }, [progress])

  if (progressSwitch) {
    return (
      <div className="work-progress between-lines-item">
        工作进度：
        <span
          className="progress-tag"
          style={{
            background: workProgress[progress].background,
            color: workProgress[progress].color
          }}
        >
          {progressText}
        </span>
      </div>
    )
  }
  return null
}

interface ResultProps {
  photos: JigsawPhoto[]
  jigsawData: JigsawData
  onChange: (data: JigsawData) => void
}

const Result = (props: ResultProps, ref: React.Ref<HTMLDivElement>) => {
  const { photos, jigsawData, onChange } = props
  const {
    type,
    title,
    subTitle,
    mainBody,
    date,
    reportor,
    mobile,
    company,
    avatarSwitch,
    copilotSwitch,
    progress,
    progressSwitch,
    themeColor,
    tableConfig
  } = jigsawData

  const showLines = useMemo(() => {
    return copilotSwitch || progressSwitch || mainBody
  }, [copilotSwitch, progressSwitch, mainBody])

  const handleTableChange = (newTableConfig) => {
    onChange({
      ...jigsawData,
      tableConfig: newTableConfig
    })
  }

  return (
    <div className="result" ref={ref}>
      <h1 className="title" style={{ color: themeColor }}>
        {title}
      </h1>
      <ReportInfo {...props} />

      <div className="lines">
        <div className="bold-line" style={{ background: themeColor }}></div>
        <div className="thin-line" style={{ background: themeColor }}></div>
      </div>
      <div className="between-lines-container">
        <AIContent photos={photos} copilotSwitch={copilotSwitch} />
        <WorkProgress progressSwitch={progressSwitch} progress={progress} />
        <Table config={tableConfig} onChange={handleTableChange} />
        <div className="main-body-wrapper between-lines-item">
          {mainBody && <pre className="main-body">{mainBody}</pre>}
        </div>
      </div>
      {showLines && (
        <div className="lines">
          <div className="thin-line" style={{ background: themeColor }}></div>
          <div className="bold-line" style={{ background: themeColor }}></div>
        </div>
      )}
      <div className={`photos layout-${type}`}>
        {photos.map((item, index) => {
          return <Photo key={index} photo={item} layout={type} />
        })}
      </div>

      <AddPhotos photos={photos} jigsawData={jigsawData} />
      <img className="logo" src={footerImg} />

      <AddPhotoTour />
    </div>
  )
}

export default forwardRef(Result)
